import React, { useState, useEffect, useMemo } from 'react'; // Import useMemo
import { Modal, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import Select from 'react-select'; // <--- Import react-select
import api from '../../services/api';
import { toast } from 'react-toastify';

const EditProjectModal = ({ show, handleClose, handleSave, project, commissionCompanies = [] }) => {
    // --- State Definitions ---
    const [formData, setFormData] = useState({
        name: '',
        picture: '',
        masterplan: '',
        startingPrice: '',
        location_id: '',
        developer_id: '',
        paymentPlans: [{ paymentplan_id: '', offerPaymentplan_id: null }],
        projectCommission: '',
        commission_company: '',
        commission_company_other: '',
        deliveryDate: '',
        isLaunch: false,
        isSponsored: false,
        gallery: [],
        incentive: '',
        incentiveType: '',
        incentiveStartDate: '',
        incentiveEndDate: '',
        nameOfAccount: '',
        bankName: '',
        iban: '',
    });

    const [originalData, setOriginalData] = useState({});
    const [locations, setLocations] = useState([]);
    const [paymentplans, setPaymentPlans] = useState([]);
    const [developers, setDevelopers] = useState([]);
    const [imagePreviewUrl, setImagePreviewUrl] = useState('');
    const [masterplanPreviewUrl, setMasterplanPreviewUrl] = useState('');
    const [galleryPreviewUrls, setGalleryPreviewUrls] = useState([]);
    const [newGalleryFiles, setNewGalleryFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasIncentive, setHasIncentive] = useState(false);
    const [dropdownsLoading, setDropdownsLoading] = useState(false); // <-- Add loading state for dropdowns

    // --- Effects ---

    // Fetch dropdown data
    useEffect(() => {
        const fetchData = async () => {
            setDropdownsLoading(true); // <-- Start loading
            try {
                // Fetch Locations
                const locRes = await api.get('/location/unpaginated');
                // *** MODIFIED: Added optional chaining ?. and fallback || [] ***
                if (locRes?.data?.status) {
                    setLocations(locRes.data.response?.data || []);
                } else {
                    console.error("Failed to fetch locations:", locRes?.data?.message || 'Unknown error');
                    toast.error(`Failed to fetch locations: ${locRes?.data?.message || 'Server error'}`);
                    setLocations([]); // Set empty array on failure
                }

                // Fetch Payment Plans
                const payRes = await api.get('/paymentplans/unpaginated');
                // *** MODIFIED: Added optional chaining ?. and fallback || [] ***
                if (payRes?.data?.status) {
                    setPaymentPlans(payRes.data.response?.data || []);
                } else {
                    console.error("Failed to fetch payment plans:", payRes?.data?.message || 'Unknown error');
                    toast.error(`Failed to fetch payment plans: ${payRes?.data?.message || 'Server error'}`);
                    setPaymentPlans([]); // Set empty array on failure
                }

                // Fetch Developers (if admin)
                const user = JSON.parse(localStorage.getItem("user") || '{}');
                if (user.type === "admin") {
                    const devRes = await api.get('/developers/unpaginated');
                    // *** MODIFIED: Added optional chaining ?. and fallback || [] ***
                    if (devRes?.data?.status) {
                        setDevelopers(devRes.data.response?.data || []);
                    } else {
                        console.error("Failed to fetch developers:", devRes?.data?.message || 'Unknown error');
                        toast.error(`Failed to fetch developers: ${devRes?.data?.message || 'Server error'}`);
                        setDevelopers([]); // Set empty array on failure
                    }
                }
                // Commission companies are passed as prop from parent (cached)
            } catch (error) {
                console.error("Error fetching dropdown data:", error);
                toast.error("Could not load necessary data for editing. Network or server issue.");
                // Reset state in case of catastrophic failure
                setLocations([]);
                setPaymentPlans([]);
                setDevelopers([]);
            } finally {
                setDropdownsLoading(false); // <-- Stop loading regardless of outcome
            }
        };

        if (show) {
            fetchData();
        } else {
            // Optional: Clear data when modal closes to avoid stale data flash
            setLocations([]);
            setPaymentPlans([]);
            setDevelopers([]);
            setDropdownsLoading(false);
        }
    }, [show]); // Keep dependency array as [show]

    // Populate form when project data is available
    useEffect(() => {
        if (project) {
            // Check if commission_company matches any company ID or name
            const commissionCompanyValue = project.commission_company || '';
            
            // First try to find by ID
            let matchedCompany = commissionCompanies.find(company => 
                String(company.id) === String(commissionCompanyValue)
            );
            
            // If not found by ID, try to find by name (case-insensitive)
            if (!matchedCompany && commissionCompanyValue) {
                matchedCompany = commissionCompanies.find(company => 
                    company.name?.toLowerCase() === String(commissionCompanyValue).toLowerCase()
                );
            }
            
            // Determine if company is in the list and get the correct ID
            const isCompanyInList = !!matchedCompany;
            const companyIdToUse = matchedCompany ? String(matchedCompany.id) : '';
            
            const initialFormData = {
                name: project.name || '',
                picture: project.picture || '',
                masterplan: project.masterplan || '',
                startingPrice: project.startingPrice?.toString() || '',
                location_id: project.location_id || '',
                developer_id: project.developer_id || '',
                paymentPlans: project.payment_plans?.map(pp => ({
                    paymentplan_id: pp.paymentplan_id || '',
                    offerPaymentplan_id: pp.offer_paymentplan_id || null
                })) || [{ paymentplan_id: '', offerPaymentplan_id: null }],
                projectCommission: project.projectCommission?.toString() || '',
                commission_company: isCompanyInList ? companyIdToUse : (commissionCompanyValue ? 'others' : ''),
                commission_company_other: isCompanyInList ? '' : commissionCompanyValue,
                deliveryDate: project.deliveryDate ? project.deliveryDate.split('T')[0] : '',
                isLaunch: !!project.isLaunch,
                isSponsored: !!project.isSponsored,
                gallery: project.gallery || [],
                incentive: project.incentive?.toString() || '',
                incentiveType: project.incentiveType || '',
                incentiveStartDate: project.incentiveStartDate ? project.incentiveStartDate.split('T')[0] : '',
                incentiveEndDate: project.incentiveEndDate ? project.incentiveEndDate.split('T')[0] : '',
                nameOfAccount: project.nameOfAccount || '',
                bankName: project.bankName || '',
                iban: project.iban || '',
            };
            setFormData(initialFormData);
            setOriginalData({ ...initialFormData });
            setHasIncentive(!!(initialFormData.incentive || initialFormData.incentiveType));
            setImagePreviewUrl(project.picture || '');
            setMasterplanPreviewUrl(project.masterplan || '');
            setGalleryPreviewUrls(project.gallery || []);
            setNewGalleryFiles([]);
        } else {
            // Reset form if project becomes null/undefined (e.g., modal closed then reopened quickly)
            setFormData({ /* initial empty state */ });
            setOriginalData({});
            setImagePreviewUrl('');
            setMasterplanPreviewUrl('');
            setGalleryPreviewUrls([]);
            setNewGalleryFiles([]);
            setHasIncentive(false);
        }
    }, [project, commissionCompanies]); // Add commissionCompanies as dependency


    // --- Transform data for react-select ---
    const locationOptions = useMemo(() => {
        // Add check for locations array
        if (!Array.isArray(locations)) return [];
        return locations.map(loc => ({
            value: loc.id,
            label: loc.name
        }));
    }, [locations]);

    // --- Handlers ---
    // Generic handler for standard inputs
    const handleChange = (e) => {
        const { id, value, type, checked, name } = e.target;
        const key = (type === 'radio' || type === 'checkbox' || type === 'switch') ? name : (id || name); // Corrected key detection for radio/checkbox/switch
        const newValue = (type === "checkbox" || type === 'switch') ? checked : value;

        setFormData((prevState) => ({
            ...prevState,
            [key]: newValue,
        }));

        // Reset bank details if isLaunch is unchecked
        if (key === 'isLaunch' && !newValue) {
            setFormData((prevState) => ({
                ...prevState,
                nameOfAccount: '',
                bankName: '',
                iban: '',
            }));
        }
    };

    // Specific handler for react-select components
    const handleSelectChange = (selectedOption, actionMeta) => {
        const { name } = actionMeta;
        setFormData((prevState) => ({
            ...prevState,
            [name]: selectedOption ? selectedOption.value : '', // Store the 'value' or empty string if cleared
        }));
    };


    // Handler for Incentive Radio Buttons
    const handleHasIncentiveChange = (e) => {
        const value = e.target.value === 'yes';
        setHasIncentive(value);
        if (!value) {
            // Clear incentive fields when "No" is selected
            setFormData(prev => ({
                ...prev,
                incentive: '',
                incentiveType: '',
                incentiveStartDate: '',
                incentiveEndDate: '',
            }));
        }
    };

    // File handlers (handleFileChange)
    const handleFileChange = (e, fieldName) => {
        const file = e.target.files[0];
        const maxSize = 2 * 1024 * 1024; // 2MB
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

        // Reset preview/data if file input is cleared
        if (!file) {
            setFormData((prev) => ({ ...prev, [fieldName]: originalData[fieldName] || '' })); // Revert to original or empty
            if (fieldName === 'picture') setImagePreviewUrl(originalData.picture || '');
            if (fieldName === 'masterplan') setMasterplanPreviewUrl(originalData.masterplan || '');
            e.target.value = null; // Ensure input is cleared visually too
            return;
        }

        // Validate type and size
        if (!allowedTypes.includes(file.type)) {
            toast.error(`Invalid file type for ${fieldName}. Allowed: JPG, PNG, JPEG`);
            e.target.value = null; // Clear invalid selection
            return;
        }
        if (file.size > maxSize) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
            toast.error(`File for ${fieldName} is too large (${fileSizeMB}MB). Please compress the image or choose a smaller file (max 2MB).`, { autoClose: 5000 });
            e.target.value = null; // Clear invalid selection
            return;
        }

        // Read and set file
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64Result = reader.result;
            setFormData((prev) => ({ ...prev, [fieldName]: base64Result })); // Store base64 string
            if (fieldName === 'picture') setImagePreviewUrl(base64Result);
            if (fieldName === 'masterplan') setMasterplanPreviewUrl(base64Result);
        };
        reader.onerror = (error) => {
            console.error("FileReader error:", error);
            toast.error(`Error reading file for ${fieldName}.`);
            // Optionally reset on error
            setFormData((prev) => ({ ...prev, [fieldName]: originalData[fieldName] || '' }));
            if (fieldName === 'picture') setImagePreviewUrl(originalData.picture || '');
            if (fieldName === 'masterplan') setMasterplanPreviewUrl(originalData.masterplan || '');
        }
        reader.readAsDataURL(file);
    };

    // Handle multiple files with 6 file limit
    const handleMultipleFilesChange = async (event) => {
        const files = Array.from(event.target.files);
        const maxSize = 2 * 1024 * 1024; // 2MB
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        const maxFiles = 6; // Max number of files
        let successfulUploads = [];
        let failedMessages = [];

        // Check total file count
        if (files.length > maxFiles) {
            toast.error(`You can only upload a maximum of ${maxFiles} gallery images at once.`);
            event.target.value = null; // Clear the selection
            return; // Stop processing
        }

        // Process each file
        const processFile = (file) => {
            return new Promise((resolve, reject) => {
                if (!allowedTypes.includes(file.type)) {
                    return reject({ file, reason: `Invalid file type: ${file.name}. Only JPG, JPEG, PNG allowed.` });
                }
                if (file.size > maxSize) {
                    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
                    return reject({ file, reason: `File "${file.name}" is too large (${fileSizeMB}MB). Please compress the image or choose a smaller file (max 2MB per image).` });
                }
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result); // Resolve with base64 string
                reader.onerror = (error) => {
                    console.error("FileReader error: ", error);
                    reject({ file, reason: `Error reading file ${file.name}.` });
                };
                reader.readAsDataURL(file);
            });
        };

        const results = await Promise.allSettled(files.map(file => processFile(file)));

        results.forEach(result => {
            if (result.status === 'fulfilled') {
                successfulUploads.push(result.value);
            } else {
                // Collect failure reasons for a summary toast
                failedMessages.push(result.reason.reason);
            }
        });

        // Update state only if there were successful uploads
        if (successfulUploads.length > 0) {
            setNewGalleryFiles(successfulUploads); // Store the base64 strings for delta check
            setGalleryPreviewUrls(successfulUploads); // Update preview with base64 URLs
            setFormData(prev => ({ ...prev, gallery: [] })); // Mark gallery as changed (will use newGalleryFiles in delta)
            toast.success(`${successfulUploads.length} gallery image(s) ready.`);
        } else if (files.length > 0) {
            // If files were selected but none were valid
            toast.warning("No valid gallery files were processed.");
            // Revert previews to original state if nothing was successful
            setGalleryPreviewUrls(originalData.gallery || []);
            setNewGalleryFiles([]);
            // Ensure formData.gallery reflects the original state if no new files are set
            setFormData(prev => ({ ...prev, gallery: originalData.gallery || [] }));
        }

        // Show summary of errors if any files failed
        if (failedMessages.length > 0) {
            const errorSummary = failedMessages.slice(0, 3).join('\n') + (failedMessages.length > 3 ? '\n...' : ''); // Show first few errors
            toast.warning(<> ${failedMessages.length} file(s) failed:<br /><pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.8em' }}>{errorSummary}</pre></>, { autoClose: 7000 });
        }

        // Always clear the input value after processing
        event.target.value = null;
    };

    // Add new function to handle payment plan changes
    const handlePaymentPlanChange = (index, field, value) => {
        setFormData(prev => {
            const newPaymentPlans = [...prev.paymentPlans];
            newPaymentPlans[index] = {
                ...newPaymentPlans[index],
                [field]: value
            };
            return {
                ...prev,
                paymentPlans: newPaymentPlans
            };
        });
    };

    // Add function to add new payment plan
    const addPaymentPlan = () => {
        if (formData.paymentPlans.length >= 4) {
            toast.warning("Maximum of 4 payment plans allowed per project.");
            return;
        }
        setFormData(prev => ({
            ...prev,
            paymentPlans: [...prev.paymentPlans, { paymentplan_id: '', offerPaymentplan_id: null }]
        }));
    };

    // Add function to remove payment plan
    const removePaymentPlan = (index) => {
        setFormData(prev => ({
            ...prev,
            paymentPlans: prev.paymentPlans.filter((_, i) => i !== index)
        }));
    };

    // Submit handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // --- Calculate Delta (Changes only) ---
        const delta = {};
        Object.keys(formData).forEach((key) => {
            // Special handling for payment plans
            if (key === 'paymentPlans') {
                const originalPlans = originalData.paymentPlans || [];
                const currentPlans = formData.paymentPlans || [];
                
                // Check if payment plans have changed
                if (JSON.stringify(originalPlans) !== JSON.stringify(currentPlans)) {
                    delta[key] = currentPlans;
                }
            }
            // Special handling for files
            else if (key === 'gallery') {
                if (newGalleryFiles.length > 0) {
                    delta[key] = newGalleryFiles;
                }
            }
            else if (key === 'picture' || key === 'masterplan') {
                const originalValue = originalData[key] || '';
                const currentValue = formData[key] || '';
                if (currentValue !== originalValue) {
                    if (typeof currentValue === 'string' && (currentValue.startsWith('data:image') || currentValue === '')) {
                        delta[key] = currentValue;
                    }
                }
            }
            else {
                const originalValue = (key === 'isLaunch' || key === 'isSponsored') ? !!originalData[key] : (originalData[key] ?? '');
                const currentValue = (key === 'isLaunch' || key === 'isSponsored') ? !!formData[key] : (formData[key] ?? '');

                if (currentValue !== originalValue) {
                    delta[key] = formData[key] === null || formData[key] === undefined ? '' : formData[key];
                }
            }
        });

        // Adjust delta for cleared incentive fields when 'Has Incentive' is toggled to 'No'
        const originalHasIncentive = !!(originalData.incentive || originalData.incentiveType);
        if (!hasIncentive && originalHasIncentive) {
            if (originalData.incentive && !delta.hasOwnProperty('incentive')) delta.incentive = '';
            if (originalData.incentiveType && !delta.hasOwnProperty('incentiveType')) delta.incentiveType = '';
            if (originalData.incentiveStartDate && !delta.hasOwnProperty('incentiveStartDate')) delta.incentiveStartDate = '';
            if (originalData.incentiveEndDate && !delta.hasOwnProperty('incentiveEndDate')) delta.incentiveEndDate = '';
        }

        // No changes detected check
        if (Object.keys(delta).length === 0) {
            toast.info("No changes detected.");
            setLoading(false);
            handleClose();
            return;
        }

        // --- Frontend Validation on Final State (Original + Delta) ---
        let isValid = true;
        const finalState = { ...originalData, ...delta };

        // Required Fields Check
        ['name', 'startingPrice', 'location_id', 'projectCommission', 'commission_company', 'deliveryDate'].forEach(field => {
            if (finalState[field] === null || finalState[field] === undefined || finalState[field] === '') {
                const fieldNameMap = {
                    name: "Name",
                    startingPrice: "Starting Price",
                    location_id: "Location",
                    projectCommission: "Project Commission",
                    commission_company: "Commission Company",
                    deliveryDate: "Delivery Date",
                    developer_id: "Developer"
                };
                toast.error(`Field "${fieldNameMap[field] || field}" cannot be empty.`);
                isValid = false;
            }
        });

        // Validate commission company
        if (!finalState.commission_company) {
            toast.error("Commission Company is required.");
            isValid = false;
        } else if (finalState.commission_company === 'others' && !finalState.commission_company_other?.trim()) {
            toast.error("Please enter the company name when selecting 'Others'.");
            isValid = false;
        } else if (finalState.commission_company === 'others' && finalState.commission_company_other?.trim().length > 30) {
            toast.error("Company name must be 30 characters or less.");
            isValid = false;
        }

        // Validate payment plans
        if (!finalState.paymentPlans || finalState.paymentPlans.length === 0) {
            toast.error("At least one payment plan is required.");
            isValid = false;
        } else {
            finalState.paymentPlans.forEach((plan, index) => {
                if (!plan.paymentplan_id) {
                    toast.error(`Payment Plan ${index + 1} is required.`);
                    isValid = false;
                }
            });
        }

        // Developer ID check for Admin
        const user = JSON.parse(localStorage.getItem("user") || '{}');
        if (user.type === "admin" && !finalState.developer_id) {
            toast.error("Developer field cannot be empty for Admin.");
            isValid = false;
        }

        // Bank details check if 'Is Launch' is true in the final state
        if (finalState.isLaunch) {
            if (!finalState.nameOfAccount || !finalState.bankName || !finalState.iban) {
                toast.error("Bank Details (Account Name, Bank Name, IBAN) are required when 'Is Launch' is selected.");
                isValid = false;
            }
        }

        // Incentive details check (use `hasIncentive` state, as it controls the section visibility)
        if (hasIncentive) {
            // Check fields in the final state if incentive is active
            const incentiveValueProvided = finalState.incentive || finalState.incentive === 0; // Check if value exists (incl. 0)
            if (incentiveValueProvided && (!finalState.incentiveType || !finalState.incentiveStartDate || !finalState.incentiveEndDate)) {
                toast.error("When providing an Incentive Value, you must also provide Type, Start Date, and End Date.");
                isValid = false;
            }
            // Optional: Check if *only* type/dates are provided without a value (might be invalid)
            // if (!incentiveValueProvided && (finalState.incentiveType || finalState.incentiveStartDate || finalState.incentiveEndDate)) {
            //    toast.error("Please provide an Incentive Value if setting Type or Dates.");
            //    isValid = false;
            // }
        }

        // Date comparison check for incentive
        if (hasIncentive && finalState.incentiveStartDate && finalState.incentiveEndDate && finalState.incentiveStartDate > finalState.incentiveEndDate) {
            toast.error("Incentive End Date cannot be earlier than the Start Date.");
            isValid = false;
        }

        if (!isValid) {
            setLoading(false);
            return;
        }

        // --- Prepare commission_company for API ---
        if (delta.hasOwnProperty('commission_company')) {
            if (delta.commission_company === 'others') {
                delta.commission_company = delta.commission_company_other || formData.commission_company_other || '';
            }
            // Remove commission_company_other from delta as it's not needed in API
            delete delta.commission_company_other;
        }

        // --- API Call ---
        try {
            if (!project?.id) {
                toast.error("Project ID is missing. Cannot update.");
                setLoading(false);
                return;
            }

            const response = await api.put(`/projects/${project.id}`, delta);

            if (response?.data?.status) {
                const isAdmin = user.type === "admin";
                const autoApprove = user.autoApprove;

                if (isAdmin || autoApprove) {
                    toast.success("Project updated successfully!");
                } else {
                    toast.success("Update submitted for approval!");
                }

                if (handleSave && typeof handleSave === "function") {
                    const updatedProjectData = response.data.response?.data ?
                        { ...project, ...response.data.response.data }
                        : finalState;

                    handleSave(updatedProjectData);
                }
                handleClose();
            } else {
                toast.error(response?.data?.message || "Failed to update project. Please try again.");
            }
        } catch (error) {
            console.error("Error updating project:", error);
            if (error.response) {
                if (error.response.status === 413) {
                    // Handle payload too large error
                    toast.error(
                        "The images you've uploaded are too big. Please reduce image sizes (compress them) or remove some images from the gallery and try again.",
                        { autoClose: 8000 }
                    );
                } else if (error.response.status === 422 && error.response.data?.response?.errors) {
                    const errors = error.response.data.response.errors;
                    const errorMessages = Object.values(errors).flat().join('\n');
                    toast.error(<>Validation Failed:<br /><pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.9em' }}>{errorMessages}</pre></>, { autoClose: 7000 });
                } else if (error.response.data?.message) {
                    // Check if the error message contains size-related keywords
                    const message = error.response.data.message.toLowerCase();
                    if (message.includes('too large') || message.includes('size') || message.includes('payload')) {
                        toast.error("Images are too large! Please compress your images or reduce their size and try again.", { autoClose: 6000 });
                    } else {
                        toast.error(`Update Failed: ${error.response.data.message}`);
                    }
                } else {
                    toast.error(`Update Failed: Server error (${error.response.status}).`);
                }
            } else if (error.request) {
                toast.error("Network Error: Could not connect to the server. Please check your connection.");
            } else {
                toast.error("An unexpected error occurred during the update.");
            }
        } finally {
            setLoading(false);
        }
    };

    // --- Render ---
    // Show loading spinner while fetching dropdowns OR if project data isn't loaded yet
    const isLoadingInitialData = dropdownsLoading || !project || Object.keys(originalData).length === 0;


    return (
        <Modal show={show} onHide={() => !loading && handleClose()} backdrop={loading ? 'static' : true} keyboard={!loading} size="lg" centered>
            <Modal.Header closeButton={!loading}>
                {/* Ensure originalData exists before accessing name */}
                <Modal.Title>Edit Project: {originalData?.name || (isLoadingInitialData ? 'Loading...' : 'Project Data Missing')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Show loading indicator for initial data fetch */}
                {isLoadingInitialData && !dropdownsLoading && !project ? (
                    <div className="text-center p-5"> <p className="mt-2">Waiting for project data...</p> </div>
                ) : isLoadingInitialData && dropdownsLoading ? (
                    <div className="text-center p-5"> <Spinner animation="border" role="status"> <span className="visually-hidden">Loading...</span> </Spinner> <p className="mt-2">Loading options...</p> </div>
                ) : !project ? (
                    <div className="text-center p-5 text-danger">Error: Project data could not be loaded.</div>
                ) : (
                    // Render form only when initial data (project) and dropdowns (attempted fetch) are ready
                    <Form onSubmit={handleSubmit} noValidate>
                        {/* --- Basic Details --- */}
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label> Name <span className="text-danger">*</span> </Form.Label>
                                    <Form.Control type="text" placeholder="Enter name" value={formData.name} onChange={handleChange} required isInvalid={!formData.name && false /* Add validation state if needed */} />
                                    <Form.Control.Feedback type="invalid">Please provide a project name.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            {/* Developer Dropdown (Admin Only) */}
                            {JSON.parse(localStorage.getItem("user") || '{}').type === "admin" && (
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="developer_id">
                                        <Form.Label> Developer <span className="text-danger">*</span> </Form.Label>
                                        <Form.Select name="developer_id" value={formData.developer_id} onChange={handleChange} required={JSON.parse(localStorage.getItem("user") || '{}').type === "admin"} disabled={developers.length === 0} >
                                            <option value="">{developers.length === 0 ? 'Loading/No developers...' : 'Select a Developer'}</option>
                                            {/* Add check for developers array */}
                                            {Array.isArray(developers) && developers.map((developer) => (
                                                <option key={developer.id} value={developer.id}> {developer.name} </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">Please select a developer.</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>
                        <Row>
                            <Col md={6}>
                                {/* Project Commission */}
                                <div className="mb-20">
                                    <label htmlFor="projectCommission" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        Project Commission (%) <span className="text-danger-600">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control radius-8"
                                        id="projectCommission"
                                        name="projectCommission"
                                        placeholder="Enter percentage (e.g., 5)"
                                        value={formData.projectCommission}
                                        onChange={handleChange}
                                        required
                                        min="0"
                                        max="100"
                                        step="0.1"
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                {/* Starting Price */}
                                <div className="mb-20">
                                    <label htmlFor="startingPrice" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        Starting Price (EGP) <span className="text-danger-600">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control radius-8"
                                        id="startingPrice"
                                        name="startingPrice"
                                        placeholder="e.g., 1,500,000"
                                        value={formData.startingPrice}
                                        onChange={handleChange}
                                        required
                                        inputMode="numeric"
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            {/* Commission Company */}
                            <Col md={12}>
                                <Form.Group className="mb-3" controlId="commission_company">
                                    <Form.Label>
                                        Commission Company <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="commission_company"
                                        value={formData.commission_company}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setFormData(prev => ({
                                                ...prev,
                                                commission_company: value,
                                                commission_company_other: value === 'others' ? prev.commission_company_other : ''
                                            }));
                                        }}
                                        required
                                        disabled={commissionCompanies.length === 0}
                                    >
                                        <option value="">
                                            {commissionCompanies.length === 0 ? 'Loading/No companies...' : 'Select Commission Company'}
                                        </option>
                                        {Array.isArray(commissionCompanies) && commissionCompanies.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.name}
                                            </option>
                                        ))}
                                        <option value="others">Others</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">Please select a commission company.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        {formData.commission_company === 'others' && (
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3" controlId="commission_company_other">
                                        <Form.Label>
                                            Company Name <span className="text-danger">*</span>
                                            <span className="text-muted fw-normal"> (Max 30 characters)</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter company name"
                                            value={formData.commission_company_other}
                                            onChange={handleChange}
                                            required
                                            maxLength={30}
                                        />
                                        <Form.Text className="text-muted">
                                            {formData.commission_company_other?.length || 0}/30 characters
                                        </Form.Text>
                                        <Form.Control.Feedback type="invalid">Please enter the company name.</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                        <Row>
                            {/* Location Select (react-select) */}
                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="location_id_group">
                                    <Form.Label htmlFor="location_id"> Location <span className="text-danger">*</span> </Form.Label>
                                    <Select
                                        inputId="location_id"
                                        name="location_id"
                                        options={locationOptions}
                                        value={locationOptions.find(option => option.value === formData.location_id) || null}
                                        onChange={handleSelectChange}
                                        placeholder={locations.length === 0 && dropdownsLoading ? "Loading..." : "Search or select..."}
                                        isClearable
                                        isDisabled={locations.length === 0}
                                        styles={{
                                            control: (baseStyles, state) => ({ ...baseStyles, borderColor: state.isFocused ? '#86b7fe' : '#dee2e6', boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none', borderRadius: '0.375rem', minHeight: '38px', backgroundColor: state.isDisabled ? '#e9ecef' : 'white' }),
                                            input: (base) => ({ ...base, margin: '0px', padding: '0px' }),
                                            valueContainer: (base) => ({ ...base, padding: '1px 8px' }),
                                            placeholder: (base) => ({ ...base, color: '#6c757d' }),
                                        }}
                                        aria-label="Location"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="deliveryDate">
                                    <Form.Label> Delivery Date <span className="text-danger">*</span> </Form.Label>
                                    <Form.Control type="date" value={formData.deliveryDate} onChange={handleChange} required />
                                    <Form.Control.Feedback type="invalid">Please select a delivery date.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Payment Plans Section - Moved here */}
                        <div className="mb-20">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-0">
                                    Payment Plans <span className="text-danger-600">*</span>
                                    <span className="text-muted ms-2">(Max 4)</span>
                                </label>
                                <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    onClick={addPaymentPlan}
                                    className="radius-8"
                                    disabled={formData.paymentPlans.length >= 4}
                                >
                                    Add Payment Plan
                                </Button>
                            </div>
                            
                            {formData.paymentPlans.map((plan, index) => (
                                <div key={index} className="border rounded p-3 mb-3 position-relative">
                                    {index > 0 && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="position-absolute top-0 end-0 m-2"
                                            onClick={() => removePaymentPlan(index)}
                                        >
                                            <i className="bi bi-x me-1"></i>
                                            Delete Payment Plan
                                        </Button>
                                    )}
                                    <Row>
                                        <Col md={12}>
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                                                    Payment Plan {index + 1} <span className="text-danger-600">*</span>
                                                </label>
                                                <select
                                                    className="form-select radius-8"
                                                    value={plan.paymentplan_id}
                                                    onChange={(e) => handlePaymentPlanChange(index, 'paymentplan_id', e.target.value)}
                                                    required
                                                >
                                                    <option value="">Select Payment Plan</option>
                                                    {paymentplans.map((pp) => (
                                                        <option key={pp.id} value={pp.id}>
                                                            {pp.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            ))}
                        </div>

                        {/* --- Incentive Section --- */}
                        <div className="p-3 my-3 border rounded border-light-subtle"> {/* Updated border class */}
                            <Form.Group className="mb-3" controlId="hasIncentiveRadioGroup">
                                <Form.Label className="fw-semibold text-primary mb-2">Has Incentive?</Form.Label> {/* Updated text color/margin */}
                                <div>
                                    <Form.Check inline type="radio" label="Yes" name="hasIncentiveRadio" id="hasIncentiveYesEdit" value="yes" checked={hasIncentive === true} onChange={handleHasIncentiveChange} />
                                    <Form.Check inline type="radio" label="No" name="hasIncentiveRadio" id="hasIncentiveNoEdit" value="no" checked={hasIncentive === false} onChange={handleHasIncentiveChange} />
                                </div>
                            </Form.Group>

                            {hasIncentive && (
                                <div className="mt-3 pt-3 border-top border-light-subtle"> {/* Added separator */}
                                    <h6 className="text-muted fw-semibold mb-3">Incentive Details</h6>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="incentive">
                                                <Form.Label> Incentive Value <span className="text-danger">*</span></Form.Label>
                                                <Form.Control type="number" placeholder="Enter value" value={formData.incentive} onChange={handleChange} min="0" required={hasIncentive} />
                                                <Form.Control.Feedback type="invalid">Required if incentive is active.</Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="incentiveTypeGroup">
                                                <Form.Label> Incentive Type <span className="text-danger">*</span></Form.Label>
                                                <div>
                                                    <Form.Check inline type="radio" label="Percentage" name="incentiveType" id="editIncentiveTypePercentage" value="percentage" checked={formData.incentiveType === "percentage"} onChange={handleChange} required={hasIncentive} />
                                                    <Form.Check inline type="radio" label="Amount" name="incentiveType" id="editIncentiveTypeAmount" value="amount" checked={formData.incentiveType === "amount"} onChange={handleChange} required={hasIncentive} />
                                                </div>
                                                {/* Basic visual validation indicator */}
                                                {!formData.incentiveType && hasIncentive && <div className="text-danger" style={{ fontSize: '0.875em', marginTop: '0.25rem' }}>Please select incentive type.</div>}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="incentiveStartDate">
                                                <Form.Label> Incentive Start Date <span className="text-danger">*</span></Form.Label>
                                                <Form.Control type="date" value={formData.incentiveStartDate} onChange={handleChange} required={hasIncentive} />
                                                <Form.Control.Feedback type="invalid">Required if incentive is active.</Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="incentiveEndDate">
                                                <Form.Label> Incentive End Date <span className="text-danger">*</span></Form.Label>
                                                <Form.Control type="date" value={formData.incentiveEndDate} onChange={handleChange} required={hasIncentive} min={formData.incentiveStartDate || ''} />
                                                <Form.Control.Feedback type="invalid">Required and cannot be before start date.</Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </div>

                        {/* --- Images Section --- */}
                        <h6 className="text-muted fw-semibold mt-4 mb-3">Project Media</h6> {/* Section Header */}
                        <Row>
                            {/* Cover Photo */}
                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="picture">
                                    <Form.Label> Cover Photo <span className="text-muted fw-normal">(Max 2MB, JPG/PNG)</span> </Form.Label>
                                    <Form.Control type="file" onChange={(e) => handleFileChange(e, 'picture')} accept="image/jpeg, image/png, image/jpg" />
                                    {imagePreviewUrl && (
                                        <div className="mt-2">
                                            <img src={imagePreviewUrl} alt="Cover Preview" style={{ maxWidth: '150px', height: 'auto', display: 'block', border: '1px solid #ddd' }} />
                                            {/* Add button to clear selection */}
                                            {formData.picture && formData.picture !== originalData.picture && (
                                                <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => {
                                                    setFormData(prev => ({ ...prev, picture: originalData.picture || '' }));
                                                    setImagePreviewUrl(originalData.picture || '');
                                                    const input = document.getElementById('picture'); if (input) input.value = null; // Clear file input
                                                }}>Revert</Button>
                                            )}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            {/* Masterplan Image */}
                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="masterplan">
                                    <Form.Label> Masterplan Image <span className="text-muted fw-normal">(Max 2MB, JPG/PNG)</span> </Form.Label>
                                    <Form.Control type="file" onChange={(e) => handleFileChange(e, 'masterplan')} accept="image/jpeg, image/png, image/jpg" />
                                    {masterplanPreviewUrl && (
                                        <div className="mt-2">
                                            <img src={masterplanPreviewUrl} alt="Masterplan Preview" style={{ maxWidth: '150px', height: 'auto', display: 'block', border: '1px solid #ddd' }} />
                                            {/* Add button to clear selection */}
                                            {formData.masterplan && formData.masterplan !== originalData.masterplan && (
                                                <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => {
                                                    setFormData(prev => ({ ...prev, masterplan: originalData.masterplan || '' }));
                                                    setMasterplanPreviewUrl(originalData.masterplan || '');
                                                    const input = document.getElementById('masterplan'); if (input) input.value = null; // Clear file input
                                                }}>Revert</Button>
                                            )}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        {/* Gallery Section */}
                        <Form.Group className="mb-3" controlId="gallery">
                            <Form.Label>
                                Gallery{' '}
                                <span className="text-muted fw-normal">
                                    (Replaces existing gallery. Max {6} files, Max 2MB each, JPG/PNG)
                                </span>
                            </Form.Label>
                            <Form.Control
                                type="file"
                                multiple
                                onChange={handleMultipleFilesChange}
                                accept="image/jpeg, image/png, image/jpg"
                            // Consider adding isInvalid styling based on validation state if needed
                            />
                            <Form.Text className="text-muted">
                                Select up to {6} images to replace the current gallery.
                            </Form.Text>
                            {/* Preview Area */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px', minHeight: '80px', background: '#f8f9fa', border: '1px dashed #ddd', padding: '5px' }}>
                                {galleryPreviewUrls.length > 0 ? (
                                    galleryPreviewUrls.map((url, index) => (
                                        <img
                                            key={index}
                                            src={url}
                                            alt={`Gallery Preview ${index + 1}`}
                                            style={{ height: '80px', width: 'auto', border: '1px solid #ccc', padding: '2px', objectFit: 'contain' }}
                                        />
                                    ))
                                ) : (
                                    <div className="text-muted p-3" style={{ fontSize: '0.9em' }}>Current gallery is empty or new images will replace it.</div>
                                )
                                }
                            </div>
                            {/* Message indicating pending change */}
                            {newGalleryFiles.length > 0 && <p className='text-info small mt-1 fst-italic'>New gallery images are selected and will replace the old ones upon saving.</p>}
                        </Form.Group>

                        {/* --- Launch & Sponsor Section --- */}
                        <h6 className="text-muted fw-semibold mt-4 mb-3">Project Status</h6> {/* Section Header */}
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="isLaunchCheck">
                                    <Form.Check type="switch" id="isLaunch" name="isLaunch" label="This is a new launch project" checked={formData.isLaunch} onChange={handleChange} />
                                    <Form.Text className="text-muted">Requires bank details if checked.</Form.Text>
                                </Form.Group>
                            </Col>
                            {/* Sponsor Checkbox (Admin Only) */}
                            {JSON.parse(localStorage.getItem("user") || '{}').type === "admin" && (
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="isSponsoredCheck">
                                        <Form.Check type="switch" id="isSponsored" name="isSponsored" label="Mark project as sponsored" checked={formData.isSponsored} onChange={handleChange} />
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>

                        {/* --- Conditional Bank Details --- */}
                        {formData.isLaunch && (
                            <div className="p-3 my-3 border rounded border-info-subtle bg-info-subtle"> {/* Use Bootstrap subtle variants */}
                                <h6 className="text-info-emphasis fw-semibold mb-3">Bank Details (Required for New Launch)</h6> {/* Use emphasis variant */}
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="nameOfAccount">
                                            <Form.Label> Account Holder Name <span className="text-danger">*</span> </Form.Label>
                                            <Form.Control type="text" placeholder="Enter account holder name" value={formData.nameOfAccount} onChange={handleChange} required={formData.isLaunch} />
                                            <Form.Control.Feedback type="invalid">Required for launch projects.</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="bankName">
                                            <Form.Label> Bank Name <span className="text-danger">*</span> </Form.Label>
                                            <Form.Control type="text" placeholder="Enter bank name" value={formData.bankName} onChange={handleChange} required={formData.isLaunch} />
                                            <Form.Control.Feedback type="invalid">Required for launch projects.</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3" controlId="iban">
                                    <Form.Label> IBAN <span className="text-danger">*</span> </Form.Label>
                                    <Form.Control type="text" placeholder="Enter IBAN" value={formData.iban} onChange={handleChange} required={formData.isLaunch} />
                                    <Form.Control.Feedback type="invalid">Required for launch projects.</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                        )}

                        {/* Footer */}
                        <Modal.Footer className="mt-4"> {/* Add margin top */}
                            <Button variant="secondary" onClick={handleClose} disabled={loading}>Close</Button>
                            <Button variant="primary" type="submit" disabled={loading || isLoadingInitialData}> {/* Disable save if still loading initial data */}
                                {loading ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" /> Saving...</> : 'Save Changes'}
                            </Button>
                        </Modal.Footer>
                    </Form>
                )
                }
            </Modal.Body>
        </Modal>
    );
};

export default EditProjectModal;